/**
 * Os contratos vindos do banco.
 *
 * Cada união aqui espelha um `create type … as enum` de
 * `supabase/migrations/0001_schema.sql` do nível 1. Espelhar é decisão: o banco
 * é a autoridade, e um valor que existe aqui mas não lá vira erro só no
 * `insert`, em produção. Quando o enum do SQL mudar, muda aqui junto — e o
 * `tsc` aponta cada lugar que precisa acompanhar.
 *
 * As listas `const` existem porque a tela precisa iterar sobre elas (paleta de
 * nós, filtro de estado) e porque derivar o tipo da lista impede que as duas
 * saiam de sincronia entre si.
 */

/* ── Enums do banco ───────────────────────────────────────────────────────── */

export const PAPEIS = ['proprietario', 'administrador', 'operador', 'leitor'] as const;
export type Papel = (typeof PAPEIS)[number];

export const ESTADOS_DE_FORMULARIO = ['rascunho', 'publicado', 'encerrado'] as const;
export type EstadoFormulario = (typeof ESTADOS_DE_FORMULARIO)[number];

export const TIPOS_DE_NO = [
  'inicio',
  'pergunta',
  'condicao',
  'mensagem',
  'acao_email',
  'acao_etiqueta',
  'acao_webhook',
  'acao_automacao',
  'fim',
] as const;
export type TipoNo = (typeof TIPOS_DE_NO)[number];

export const SUBTIPOS_DE_PERGUNTA = [
  'texto_curto',
  'texto_longo',
  'escolha_unica',
  'escolha_multipla',
  'sim_nao',
  'numero',
  'data',
  'hora',
  'email',
  'telefone',
  'escala',
] as const;
export type SubtipoPergunta = (typeof SUBTIPOS_DE_PERGUNTA)[number];

export const ESTADOS_DE_RESPOSTA = ['em_andamento', 'concluida', 'abandonada'] as const;
export type EstadoResposta = (typeof ESTADOS_DE_RESPOSTA)[number];

export const ESTADOS_DE_ENVIO = ['pendente', 'enviado', 'falhou', 'cancelado'] as const;
export type EstadoEnvio = (typeof ESTADOS_DE_ENVIO)[number];

/** Os nós que disparam ao serem alcançados e não interrompem o convidado. */
export const TIPOS_DE_ACAO = [
  'acao_email',
  'acao_etiqueta',
  'acao_webhook',
  'acao_automacao',
] as const;
export type TipoAcao = (typeof TIPOS_DE_ACAO)[number];

/* ── Condição de aresta ───────────────────────────────────────────────────── */

export const OPERADORES = [
  'igual',
  'diferente',
  'contem',
  'nao_contem',
  'maior',
  'maior_igual',
  'menor',
  'menor_igual',
  'vazio',
  'preenchido',
  'entre',
] as const;
export type Operador = (typeof OPERADORES)[number];

/** Valor de resposta: o que cabe num `jsonb` de `resposta_item.valor`. */
export type Valor = string | number | boolean | readonly string[] | null;

export type Condicao = {
  /** `no_id` da pergunta que a condição consulta. */
  readonly campo: string;
  readonly operador: Operador;
  readonly valor?: Valor;
};

/* ── Marca (customização visual por formulário) ───────────────────────────── */

export const MODOS = ['claro', 'escuro', 'auto'] as const;
export type Modo = (typeof MODOS)[number];

export const FONTES = ['sistema', 'serifada', 'mono'] as const;
export type Fonte = (typeof FONTES)[number];

export type Marca = {
  /** Um dos 20 temas validados da Régua. Continua valendo mesmo com `acento`. */
  readonly tema: string;
  readonly modo: Modo;
  readonly fonte: Fonte;
  readonly logo_url: string | null;
  readonly capa_url: string | null;
  /**
   * A cor livre — a evolução que a §12 previa, com a fechadura que ela exigia.
   *
   * Hexadecimal canônico (cerquilha, 6 dígitos, minúsculo) ou `null`. Quando
   * preenchido, ele SOBREPÕE o acento do tema no convite; `tema` continua
   * gravado e é para onde o formulário volta quando a cor livre sai. É por
   * isso que ele é sobreposição e não substituição: remover a cor livre não
   * pode exigir uma segunda escolha de quem só quis desfazer.
   *
   * Opcional porque a coluna é `jsonb` e **linha antiga volta sem a chave** —
   * o default da coluna só age em `insert`. Quem lê passa por
   * `servicos/marca.ts:normalizarMarca`, que preenche o que faltar.
   *
   * O que este campo NÃO é: um caminho para publicar um convite ilegível.
   * `avaliarAcento` recusa antes de gravar, dizendo o par, o número e o modo.
   */
  readonly acento?: string | null;
  /**
   * O vídeo de abertura: um link de PLAYER (embed), não da página do vídeo.
   * O convidado vê o player na primeira tela do formulário, antes da
   * primeira pergunta — nunca nas seguintes. `null` quando não há vídeo.
   *
   * Opcional pela mesma razão de `acento`: a coluna é `jsonb` e uma linha
   * gravada antes da migração 0012 volta sem esta chave. Quem lê passa por
   * `servicos/marca.ts:normalizarMarca`.
   *
   * O que este campo NÃO é: um `<iframe>` livre. A CHECK `formulario_video_valido`
   * (migração 0012) só aceita `https://` e uma lista BRANCA de hosts de player —
   * hoje `iframe.mediadelivery.net`, `www.youtube.com/embed/` e
   * `player.vimeo.com/video/`. Um endereço que não passe nunca chega a ser
   * gravado; `servicos/marca.ts:enderecoDeVideoPermitido` repete a mesma lista
   * do lado da tela, pelo mesmo motivo de `util/acao.ts` repetir a de
   * `no_acao_valida`: a tela precisa dizer não enquanto se digita, e o banco é
   * quem decide de verdade.
   */
  readonly video_url?: string | null;
};

/* ── Linhas ───────────────────────────────────────────────────────────────── */

export type Organizacao = {
  readonly id: string;
  readonly nome: string;
  readonly criado_em: string;
};

export type Perfil = {
  readonly usuario_id: string;
  readonly organizacao_id: string;
  readonly nome: string;
  readonly papel: Papel;
  readonly ativo: boolean;
  readonly criado_em: string;
};

export type Formulario = {
  readonly id: string;
  readonly organizacao_id: string;
  readonly titulo: string;
  readonly descricao: string;
  readonly estado: EstadoFormulario;
  readonly token_publico: string;
  readonly marca: Marca;
  readonly abertura_em: string | null;
  readonly encerramento_em: string | null;
  readonly limite_respostas: number | null;
  readonly versao_publicada_id: string | null;
  readonly criado_por: string | null;
  readonly criado_em: string;
  readonly atualizado_em: string;
};

export type FormularioVersao = {
  readonly id: string;
  readonly formulario_id: string;
  readonly numero: number;
  readonly publicada_em: string | null;
  readonly publicada_por: string | null;
  readonly criado_em: string;
};

/**
 * `config` guarda o que é específico do tipo e por isso não virou coluna:
 *   pergunta       `{ minimo, maximo, mascara }`
 *   acao_email     `{ destinatario, assunto, corpo }`
 *   acao_etiqueta  `{ etiqueta }`
 *   acao_webhook   `{ url, corpo }`
 *   fim            `{ mensagem, instrucoes, acao }`
 */
export type ConfigNo = Readonly<Record<string, unknown>>;

/**
 * O botão da tela final: `config.acao` de um nó `fim` (migração 0007).
 *
 * É a **única** chave de `config` que sai do banco para ser clicada, e por isso
 * a única cujo conteúdo é validado pelo banco e não só pela tela — a CHECK
 * `no_acao_valida` aceita `https:`, `mailto:` e `tel:` e recusa o resto.
 * `javascript:` num botão que o organizador digita e o convidado clica é XSS
 * entregue de bandeja, e a tela é trocável enquanto o banco não é.
 *
 * `pendente` não está aqui de propósito: ela é contabilidade do construtor (um
 * endereço de exemplo que um modelo deixou por completar), `no_para_convidado`
 * não a entrega, e este tipo descreve o que o convidado recebe. Quem precisa
 * dela no painel lê `config.acao.pendente` por `acaoPendente()`, em
 * `util/acao.ts`.
 */
export type AcaoDoFim = {
  readonly rotulo: string;
  readonly url: string;
};

/**
 * Opção de `escolha_unica` / `escolha_multipla`: **string simples**, decidido
 * pelo lead. `["sim", "não", "talvez"]`.
 *
 * O que costuma justificar o par `{valor, rotulo}` é o rótulo poder mudar sem
 * quebrar o que já foi gravado — e isso o versionamento já resolve: publicar
 * congela a versão (§5) e a resposta pertence à versão que percorreu, então
 * dentro de uma versão o rótulo não muda. Duas chaves para resolver o que já
 * está resolvido é complexidade sem contrapartida.
 *
 * E o simples ganha: o valor gravado **é** o texto que a pessoa viu, então a
 * planilha diz `"não"` em vez de `opt_2`. Para quem exporta para decidir quem
 * vem ao jantar, é a diferença entre útil e inútil.
 *
 * O `valor_entre_opcoes` do banco compara tolerando os dois formatos, com teste
 * travando a tolerância — então mudar de ideia é possível, mas exige migração
 * do jsonb. i18n está fora de escopo (§12).
 */
export type Opcao = string;

export type No = {
  readonly id: string;
  readonly versao_id: string;
  readonly tipo: TipoNo;
  /** Não-nulo se, e só se, `tipo === 'pergunta'` — o banco impõe o "só se". */
  readonly subtipo: SubtipoPergunta | null;
  readonly rotulo: string;
  readonly ajuda: string;
  readonly obrigatorio: boolean;
  readonly opcoes: readonly Opcao[];
  readonly config: ConfigNo;
  readonly x: number;
  readonly y: number;
  readonly criado_em: string;
};

/** `condicao: null` é o SENÃO — o caminho quando nenhuma outra casou. */
export type Aresta = {
  readonly id: string;
  readonly versao_id: string;
  readonly origem_id: string;
  readonly destino_id: string;
  readonly condicao: Condicao | null;
  readonly ordem: number;
  readonly criado_em: string;
};

export type Grafo = {
  readonly versao: FormularioVersao;
  readonly nos: readonly No[];
  readonly arestas: readonly Aresta[];
};

export type Convite = {
  readonly id: string;
  readonly formulario_id: string;
  readonly nome: string;
  readonly email: string | null;
  readonly token: string;
  readonly enviado_em: string | null;
  readonly respondido_em: string | null;
  readonly criado_em: string;
};

export type Resposta = {
  readonly id: string;
  readonly formulario_id: string;
  readonly versao_id: string;
  readonly convite_id: string | null;
  readonly estado: EstadoResposta;
  readonly no_atual_id: string | null;
  readonly etiquetas: readonly string[];
  readonly editada: boolean;
  readonly ip_hash: string | null;
  readonly agente: string | null;
  readonly iniciada_em: string;
  readonly concluida_em: string | null;
};

export type RespostaItem = {
  readonly id: string;
  readonly resposta_id: string;
  readonly no_id: string;
  readonly valor: Valor;
  readonly respondido_em: string;
};

export type RespostaPercurso = {
  readonly id: number;
  readonly resposta_id: string;
  readonly no_id: string;
  readonly aresta_id: string | null;
  readonly em: string;
};

export type Envio = {
  readonly id: string;
  readonly resposta_id: string;
  readonly no_id: string;
  readonly canal: string;
  readonly destinatario: string;
  readonly assunto: string;
  readonly corpo: string;
  readonly estado: EstadoEnvio;
  readonly tentativas: number;
  readonly erro: string | null;
  readonly criado_em: string;
  readonly enviado_em: string | null;
};

export type Auditoria = {
  readonly id: number;
  readonly organizacao_id: string | null;
  readonly ator_id: string | null;
  readonly acao: string;
  readonly entidade: string;
  readonly entidade_id: string | null;
  readonly antes: unknown;
  readonly depois: unknown;
  readonly em: string;
};

/* ── A superfície pública do convite ────────────────────────────────────────
 *
 * Espelha `0005_api_publica.sql` do nível 1, que é a autoridade. As três
 * funções devolvem UM objeto jsonb — nem `returns table`, nem array, nem `[0]`.
 */

/**
 * O nó como o CONVIDADO o recebe. É um tipo à parte do `No` do painel, e não um
 * `Pick`, porque a diferença é de segurança e não de conveniência:
 *
 *  - não vem `versao_id`, `x`, `y` — posição no canvas é assunto da prancheta;
 *  - `config` é lista branca no servidor (`no_para_convidado`): `fim` entrega
 *    `{mensagem, instrucoes}`, `mensagem` entrega `{mensagem}`, `pergunta`
 *    entrega `{minimo, maximo, mascara}`, e qualquer outro tipo entrega `{}`.
 *    URL de webhook, corpo e destinatário de e-mail nunca saem do banco.
 *
 * Na prática o `tipo` que chega aqui é `pergunta`, `mensagem` ou `fim`: os
 * demais não param o percurso.
 */
export type NoDoConvite = {
  readonly id: string;
  readonly tipo: TipoNo;
  readonly subtipo: SubtipoPergunta | null;
  readonly rotulo: string;
  readonly ajuda: string;
  readonly obrigatorio: boolean;
  readonly opcoes: readonly Opcao[];
  readonly config: ConfigNo;
};

/**
 * O envelope de sucesso — o MESMO para as três funções.
 *
 * Título, descrição e marca voltam em toda etapa de propósito: quem recarrega a
 * página no meio do formulário não pode perder o tema nem o cabeçalho.
 *
 * `titulo` é o do FORMULÁRIO. O título da tela final é `no.rotulo` — confundir
 * os dois faz toda tela de fim repetir o nome do evento no lugar da despedida.
 */
export type EnvelopeDoConvite = {
  readonly ok: true;
  /** O segredo daquela resposta. Vai em `sessionStorage`, nunca em cookie. */
  readonly resposta_token: string;
  readonly titulo: string;
  readonly descricao: string;
  readonly marca: Marca;
  /** `concluida`, feminino: concorda com "resposta". */
  readonly concluida: boolean;
  readonly no: NoDoConvite | null;
};

/**
 * `concluir` acrescenta duas chaves de topo: são `no.config.mensagem` e
 * `no.config.instrucoes` promovidas, para a tela final não cavar dentro de
 * `config`.
 */
export type FimDoConvite = EnvelopeDoConvite & {
  readonly mensagem: string | null;
  readonly instrucoes: string | null;
};

/**
 * Uma linha da trilha, como o BANCO a devolve em `retomar`.
 *
 * É um tipo à parte do `Trilho` de `useConvite` de propósito: aqui os nomes são
 * os do jsonb (`no_id`), lá são os da tela (`noId`). A conversão mora num lugar
 * só — sem isso, `no_id` vazaria para dentro dos componentes e a fronteira entre
 * o que é contrato de banco e o que é forma de tela deixaria de existir.
 */
export type TrilhoDoBanco = {
  readonly no_id: string;
  readonly rotulo: string;
  readonly subtipo: SubtipoPergunta | null;
  readonly valor: Valor;
};

/**
 * `retomar` (0010) — a quarta porta pública, e a que existe por causa do F5.
 *
 * O defeito que ela conserta: o gancho guardava o `resposta_token` e, ao
 * recarregar, chamava `concluir`. `concluir` só devolve envelope quando a
 * resposta CHEGOU AO FIM — no meio do caminho ela recusa com `incompleta`, o
 * gancho descartava o token e abria um formulário NOVO. Quem respondeu quatro de
 * seis perguntas e girou a tela voltava do zero.
 *
 * Ela acrescenta `trilha`: o que aquele convidado já respondeu, na ordem do
 * percurso. Devolver isso não abre nada — é o dado dele, que ele acabou de
 * digitar — e sem ela o F5 devolveria a pergunta certa com o resumo em branco,
 * que o convidado lê como perda.
 *
 * As outras três chamadas NÃO trazem `trilha`, e é decisão: durante o percurso a
 * tela acumula localmente, e mandar a lista inteira a cada resposta seria pagar
 * o mesmo dado várias vezes no 3G de quem confirma presença no ônibus.
 */
export type RetomadaDoConvite = EnvelopeDoConvite & {
  readonly trilha: readonly TrilhoDoBanco[];
};

export const MOTIVOS_DE_RECUSA = [
  'nao_encontrado',
  'nao_publicado',
  'encerrado',
  'fora_da_janela',
  'limite_atingido',
  /**
   * Todas as vagas estão em preenchimento AGORA (0010).
   *
   * Separado de `limite_atingido` porque as duas frases pedem coisas
   * diferentes de quem lê: "já recebeu o número máximo" é definitivo e manda a
   * pessoa embora; esta manda voltar em alguns minutos.
   *
   * O motivo existe porque o limite virou RESERVA. Antes ele contava só
   * respostas concluídas, e por isso não limitava nada: um link que vai para
   * 500 pessoas tem 300 delas dentro antes de a primeira terminar, e as 300
   * concluem. Agora a resposta em andamento ocupa vaga enquanto está viva e
   * devolve a vaga quando esfria — e este é o motivo que aparece enquanto ela
   * está ocupada por outra pessoa.
   */
  'limite_reservado',
  'ja_respondido',
  'sessao_invalida',
  'ja_encerrada',
  'fora_de_ordem',
  'obrigatorio',
  /**
   * Valor fora das opções, em `escolha_unica` e `escolha_multipla`.
   *
   * Separado de `obrigatorio` de propósito: "escolha uma das opções" para quem
   * não escolheu nada é a mensagem errada. Vazio continua sendo `obrigatorio`.
   *
   * Em múltipla, um item inválido recusa o array inteiro — não há recusa
   * parcial, porque aceitar o resto gravaria uma resposta que a pessoa não deu.
   *
   * **Caixa importa: `'Sim'` é recusado quando a opção é `'sim'`.** Se a tela
   * de escolha normalizar caixa em qualquer ponto, ela diverge do banco e o
   * convidado vê uma recusa que não consegue resolver clicando. Submeta a
   * string da opção como ela veio.
   */
  'valor_invalido',
  'incompleta',
] as const;

export type MotivoConhecido = (typeof MOTIVOS_DE_RECUSA)[number];

/**
 * A união é **aberta** de propósito: `MotivoConhecido` dá autocomplete e pega
 * erro de digitação, e o `(string & {})` deixa um motivo novo do banco ser
 * representável sem virar mentira de tipo.
 *
 * O banco ganha motivo sem pedir licença ao front — `valor_invalido` já está
 * em discussão. Fechar a união faria o `tsc` afirmar que ele não existe: um
 * `switch` exaustivo passaria na compilação e cairia no caso errado em
 * produção, para um convidado. Com a união aberta, o compilador exige o
 * `default` — que é onde a `mensagem` do banco deve ser mostrada como está.
 *
 * Não trate motivo desconhecido como defeito: a `mensagem` vem escrita para
 * ser lida, e ela é o contrato de verdade com o convidado.
 */
export type MotivoDeRecusa = MotivoConhecido | (string & {});

/**
 * Recusa de regra de negócio. Volta com HTTP **200** — `raise exception` viraria
 * 500 e o convidado leria "algo deu errado" no lugar de "as respostas
 * encerraram na sexta".
 *
 * **`mensagem` é mostrada como está.** Não reescreva no front e não mapeie
 * `motivo` para um texto seu: a data da janela e o nome da pergunta vêm
 * interpolados do banco, e um texto local nasce desatualizado.
 *
 * `no` vem em `ja_encerrada` (duplo clique no último botão), `fora_de_ordem`
 * (aba velha) e `obrigatorio`. Nos três a tela re-renderiza o `no` que veio —
 * sem uma segunda chamada.
 */
export type RecusaDoConvite = {
  readonly ok: false;
  readonly motivo: MotivoDeRecusa;
  readonly mensagem: string;
  readonly no?: NoDoConvite | null;
  readonly concluida?: boolean;
  /**
   * O instante da janela, em `fora_da_janela` (0010).
   *
   * A data SAIU da `mensagem`, e é correção. `to_char()` no banco formata no
   * fuso do SERVIDOR, que no Supabase é UTC: o convidado no Brasil lia três
   * horas a mais e achava que ainda dava tempo — a pior forma de errar, porque
   * ele não vê erro nenhum, só perde o prazo. Quem sabe o fuso dele é o
   * navegador, e só ele.
   *
   * Então a `mensagem` continua completa e verdadeira SEM o instante ("As
   * respostas deste formulário já encerraram."), e o instante vem aqui para a
   * tela acrescentar a frase com `dataHora()`. Um front que ignore `quando`
   * mostra menos, e não mostra errado — que é o que mantém a regra do §7.3
   * ("a mensagem é mostrada como está") verdadeira.
   */
  readonly quando?: string | null;
  /** Qual beira da janela: `abertura` ou `encerramento`. */
  readonly lado?: 'abertura' | 'encerramento' | null;
};

export type ResultadoDoConvite = EnvelopeDoConvite | RecusaDoConvite;
export type ResultadoDoFim = FimDoConvite | RecusaDoConvite;
export type ResultadoDaRetomada = RetomadaDoConvite | RecusaDoConvite;

/* ── Defeito de validação de grafo ────────────────────────────────────────── */

export type DefeitoDoGrafo = {
  readonly codigo: string;
  readonly no_id: string | null;
  readonly motivo: string;
};

/* ── O relatório do painel (0006) ───────────────────────────────────────────
 *
 * A tela não soma. Tudo aqui vem pronto de função SQL, e a única coisa que o
 * front faz com estes números é formatá-los.
 *
 * Cuidado com a forma do retorno: **só `painel_formulario` é objeto**. As
 * outras três são `returns table`, e o PostgREST devolve array — o inverso das
 * três funções públicas do convite.
 */

/**
 * As duas taxas são **fração entre 0 e 1**, nunca porcentagem, e são
 * `number | null`.
 *
 * `null` quer dizer que não há denominador: `taxa_conclusao` com `total = 0`,
 * `taxa_resposta` com `convites_enviados = 0`. A tela mostra `—` — é o que
 * `porcento()` já faz sozinho.
 *
 * **`?? 0` neste campo é o bug.** `taxa_resposta: 0` é legítimo e quer dizer
 * outra coisa: convite enviado, ninguém respondeu. Deixe o `tsc` obrigar o
 * tratamento do `null` em vez de apagar a diferença com um default.
 */
export type PainelDoFormulario = {
  readonly total: number;
  readonly concluidas: number;
  readonly em_andamento: number;
  readonly abandonadas: number;
  readonly taxa_conclusao: number | null;
  readonly convites_enviados: number;
  readonly convites_respondidos: number;
  readonly taxa_resposta: number | null;
  /** `{}` quando vazio, nunca `null`. */
  readonly por_etiqueta: Readonly<Record<string, number>>;
  /** `[]` quando vazio, nunca `null`. */
  readonly por_fim: readonly {
    readonly no_id: string;
    readonly rotulo: string;
    readonly total: number;
  }[];
  /** A fila aguardando transporte (§9). O painel é obrigado a mostrá-la. */
  readonly envios_pendentes: number;
  readonly envios_falhos: number;
  /** "12 pendentes" não diz se travou e-mail ou webhook, e a providência difere. */
  readonly envios_pendentes_por_canal: Readonly<Record<string, number>>;
};

export type ItemDaResposta = {
  readonly no_id: string;
  readonly rotulo: string;
  readonly subtipo: SubtipoPergunta | null;
  /** O jsonb cru — use quando precisar do tipo. */
  readonly valor: Valor;
  /** O mesmo valor já legível — use para exibir. */
  readonly texto: string | null;
};

export type LinhaDaFila = {
  readonly resposta_id: string;
  readonly versao_numero: number;
  readonly estado: EstadoResposta;
  readonly etiquetas: readonly string[];
  /** A marca do §10: resposta corrigida pelo painel. Mostrar não é opcional. */
  readonly editada: boolean;
  readonly convite_nome: string | null;
  readonly convite_email: string | null;
  readonly iniciada_em: string;
  readonly concluida_em: string | null;
  /** `null` enquanto a resposta não chegou ao fim. */
  readonly fim_rotulo: string | null;
  /** Onde ela parou. */
  readonly no_atual_rotulo: string | null;
  /** Na ordem em que as perguntas são feitas no grafo. */
  readonly itens: readonly ItemDaResposta[];
  /**
   * O total DEPOIS do filtro e ANTES do limite, repetido em toda linha. Pedir
   * de novo com outra query é onde os dois números começam a divergir.
   */
  readonly total_filtrado: number;
};

/**
 * Note o que a fila **não** traz: o `token` da resposta. É o segredo de sessão
 * do convidado, e um operador com ele responderia no lugar da pessoa.
 */
export type FiltroDaFila = {
  readonly estado?: EstadoResposta | null;
  readonly etiqueta?: string | null;
  readonly limite?: number;
  readonly deslocamento?: number;
};

export type ColunaDeExportacao = {
  readonly versao_id: string;
  readonly versao_numero: number;
  readonly no_id: string;
  readonly ordem: number;
  readonly rotulo: string;
};

/** Como a fila, mas com `itens` **objeto** chaveado por rótulo, e sem paginação. */
export type LinhaDeExportacao = {
  readonly resposta_id: string;
  readonly versao_numero: number;
  readonly estado: EstadoResposta;
  readonly etiquetas: readonly string[];
  readonly editada: boolean;
  readonly convite_nome: string | null;
  readonly convite_email: string | null;
  readonly iniciada_em: string;
  readonly concluida_em: string | null;
  readonly fim_rotulo: string | null;
  /** `{ "Você vem?": "sim" }` — a chave é o mesmo `rotulo` de `ColunaDeExportacao`. */
  readonly itens: Readonly<Record<string, Valor>>;
};
